import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../../hooks/useAuth";
import { useProviderOwnerDashboard } from "../../provider-owner.hooks";
import type { ProviderOwnerChecklistItemDto } from "../../provider-owner.types";
import {
  isProviderOwnerApproved,
  isProviderOwnerEditable,
  isProviderOwnerUnderReview,
} from "../../provider-owner.state";
import {
  providerChecklistCompletionPercent,
  providerOwnerStatusLabel,
  providerOwnerStatusTone,
  providerPendingBlockers,
  providerStatusSummary,
} from "../../provider-owner.utils";

const sectionStyle: React.CSSProperties = {
  display: "grid",
  gap: "1rem",
};

const cardStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 18,
  padding: "1rem",
};

const actionLinkStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  textDecoration: "none",
  border: "1px solid #d1d5db",
  borderRadius: 12,
  padding: "0.8rem 1rem",
  background: "#ffffff",
  color: "#111827",
  fontWeight: 700,
};

const primaryActionStyle: React.CSSProperties = {
  ...actionLinkStyle,
  background: "#111827",
  color: "#ffffff",
  border: "1px solid #111827",
};

const statGridStyle: React.CSSProperties = {
  display: "grid",
  gap: "1rem",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
};

const onboardingCardGridStyle: React.CSSProperties = {
  display: "grid",
  gap: "0.75rem",
};

const statusChipStyle: React.CSSProperties = {
  borderRadius: 999,
  padding: "0.35rem 0.8rem",
  fontSize: "0.82rem",
  fontWeight: 700,
};

const checklistListStyle: React.CSSProperties = {
  display: "grid",
  gap: "0.6rem",
  margin: "0.9rem 0 0",
  padding: 0,
  listStyle: "none",
};

const checklistItemStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: "0.75rem",
  alignItems: "center",
  padding: "0.75rem 0.85rem",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  background: "#fafafa",
};

const checklistBadgeBaseStyle: React.CSSProperties = {
  borderRadius: 999,
  padding: "0.28rem 0.65rem",
  fontSize: "0.75rem",
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const getToneColor = (tone: "neutral" | "info" | "success" | "warning" | "danger") => {
  if (tone === "success") return "#166534";
  if (tone === "warning") return "#92400e";
  if (tone === "danger") return "#b91c1c";
  if (tone === "info") return "#1d4ed8";
  return "#374151";
};

const getToneBackground = (
  tone: "neutral" | "info" | "success" | "warning" | "danger",
) => {
  if (tone === "success") return "#dcfce7";
  if (tone === "warning") return "#fffbeb";
  if (tone === "danger") return "#fef2f2";
  if (tone === "info") return "#eff6ff";
  return "#f3f4f6";
};

const ProviderDashboardHomePage: React.FC = () => {
  const { user } = useAuth();
  const providerId = user?.providerId ?? "";

  const { snapshotQuery, statusQuery, isLoading, isError } =
    useProviderOwnerDashboard(providerId);

  const snapshot = snapshotQuery.data;
  const status = statusQuery.data ?? snapshot?.status;

  const checklist: ProviderOwnerChecklistItemDto[] = status?.checklist ?? [];
  const completionPercent =
    status?.overallCompletionPercent ?? providerChecklistCompletionPercent(checklist);
  const pendingBlockers =
    status?.pendingBlockers ?? providerPendingBlockers(checklist);

  const tone = status ? providerOwnerStatusTone(status.status) : "neutral";
  const toneColor = getToneColor(tone);
  const toneBackground = getToneBackground(tone);

  const editable = status ? isProviderOwnerEditable(status.status) : true;
  const underReview = status ? isProviderOwnerUnderReview(status.status) : false;
  const approved = status ? isProviderOwnerApproved(status.status) : false;

  const primaryOnboardingAction = editable
    ? { label: "Continue onboarding", to: "/provider/onboarding/start" }
    : underReview
    ? { label: "View review status", to: "/provider/dashboard/onboarding-status" }
    : approved
    ? { label: "Open onboarding status", to: "/provider/dashboard/onboarding-status" }
    : { label: "Open onboarding", to: "/provider/dashboard/onboarding-status" };

  const quickActions = [
    primaryOnboardingAction,
    { label: "Update profile", to: "/provider/dashboard/profile" },
    { label: "Add service", to: "/provider/dashboard/services/new" },
    { label: "Add stylist", to: "/provider/dashboard/stylists/new" },
    { label: "Open calendar", to: "/provider/dashboard/calendar" },
    { label: "Portfolio", to: "/provider/dashboard/portfolio" },
  ];

  return (
    <div style={sectionStyle}>
      <section style={cardStyle}>
        <h2 style={{ margin: 0, color: "#0f172a" }}>
          {snapshot?.profile.businessName || "Provider dashboard"}
        </h2>
        <p style={{ margin: "0.6rem 0 0", color: "#475569", lineHeight: 1.7 }}>
          Use this workspace to move from account creation to onboarding completion,
          operational setup, and later live booking management.
        </p>

        {status ? (
          <div
            style={{
              marginTop: "0.8rem",
              padding: "0.85rem",
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: "#f9fafb",
              display: "flex",
              justifyContent: "space-between",
              gap: "0.75rem",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div style={{ display: "grid", gap: "0.35rem" }}>
              <div style={{ fontSize: 13, color: "#6b7280" }}>Status</div>
              <span
                style={{
                  ...statusChipStyle,
                  color: toneColor,
                  background: toneBackground,
                  width: "fit-content",
                }}
              >
                {providerOwnerStatusLabel(status.status)}
              </span>
            </div>

            <div style={{ fontSize: 13, color: "#6b7280" }}>
              Completion: {completionPercent.toFixed(0)}% · Pending blockers:{" "}
              {pendingBlockers}
            </div>
          </div>
        ) : null}
      </section>

      <section style={statGridStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: "0.9rem", color: "#64748b", fontWeight: 600 }}>
            Media items
          </div>
          <div
            style={{
              marginTop: "0.55rem",
              fontSize: "2rem",
              color: "#0f172a",
              fontWeight: 800,
            }}
          >
            {snapshot?.mediaCount ?? 0}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontSize: "0.9rem", color: "#64748b", fontWeight: 600 }}>
            Services
          </div>
          <div
            style={{
              marginTop: "0.55rem",
              fontSize: "2rem",
              color: "#0f172a",
              fontWeight: 800,
            }}
          >
            {snapshot?.pricingCount ?? 0}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontSize: "0.9rem", color: "#64748b", fontWeight: 600 }}>
            Active team
          </div>
          <div
            style={{
              marginTop: "0.55rem",
              fontSize: "2rem",
              color: "#0f172a",
              fontWeight: 800,
            }}
          >
            {snapshot?.activeTeamCount ?? 0}
          </div>
        </div>
      </section>

      <section style={cardStyle}>
        <div style={onboardingCardGridStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "0.75rem",
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <div>
              <h3 style={{ margin: 0, color: "#0f172a" }}>Onboarding overview</h3>
              <p style={{ margin: "0.55rem 0 0", color: "#4b5563", lineHeight: 1.65 }}>
                {status
                  ? providerStatusSummary(status)
                  : "Track your onboarding progress, complete blocking sections, and move into internal review."}
              </p>
            </div>

            {status ? (
              <span
                style={{
                  ...statusChipStyle,
                  color: toneColor,
                  background: toneBackground,
                }}
              >
                {providerOwnerStatusLabel(status.status)}
              </span>
            ) : null}
          </div>

          {isLoading ? (
            <p style={{ margin: 0, color: "#6b7280" }}>Loading onboarding status…</p>
          ) : isError ? (
            <p style={{ margin: 0, color: "#b91c1c" }}>
              We could not load your onboarding status right now.
            </p>
          ) : status ? (
            <>
              <div style={{ fontSize: "0.92rem", color: "#64748b" }}>
                Completion: {completionPercent.toFixed(0)}% · Pending blockers:{" "}
                {pendingBlockers}
              </div>

              <ul style={checklistListStyle}>
                {checklist.length === 0 ? (
                  <li style={{ color: "#6b7280" }}>
                    No onboarding checklist items are available yet.
                  </li>
                ) : (
                  checklist
                    .slice(0, 5)
                    .map((item: ProviderOwnerChecklistItemDto) => {
                      const badgeStyle: React.CSSProperties = item.completed
                        ? {
                            ...checklistBadgeBaseStyle,
                            background: "#dcfce7",
                            color: "#166534",
                          }
                        : item.blocking
                        ? {
                            ...checklistBadgeBaseStyle,
                            background: "#fef2f2",
                            color: "#b91c1c",
                          }
                        : {
                            ...checklistBadgeBaseStyle,
                            background: "#f3f4f6",
                            color: "#374151",
                          };

                      return (
                        <li key={item.key} style={checklistItemStyle}>
                          <div style={{ display: "grid", gap: "0.2rem" }}>
                            <span
                              style={{
                                fontWeight: 600,
                                color: "#111827",
                                fontSize: "0.94rem",
                              }}
                            >
                              {item.label}
                            </span>
                            {item.note ? (
                              <span style={{ color: "#6b7280", fontSize: "0.84rem" }}>
                                {item.note}
                              </span>
                            ) : null}
                          </div>

                          <span style={badgeStyle}>
                            {item.completed
                              ? "Complete"
                              : item.blocking
                              ? "Blocking"
                              : "Optional"}
                          </span>
                        </li>
                      );
                    })
                )}
              </ul>

              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                  marginTop: "0.25rem",
                }}
              >
                <Link to={primaryOnboardingAction.to} style={primaryActionStyle}>
                  {primaryOnboardingAction.label}
                </Link>
                <Link
                  to="/provider/dashboard/onboarding-status"
                  style={actionLinkStyle}
                >
                  Full onboarding status
                </Link>
                <Link to="/provider/onboarding/review" style={actionLinkStyle}>
                  Review and submit
                </Link>
              </div>
            </>
          ) : null}
        </div>
      </section>

      <section style={cardStyle}>
        <h3 style={{ margin: 0, color: "#0f172a" }}>Next actions</h3>

        {isLoading ? (
          <p style={{ margin: "0.6rem 0 0", color: "#6b7280" }}>
            Loading provider status…
          </p>
        ) : isError ? (
          <p style={{ margin: "0.6rem 0 0", color: "#b91c1c" }}>
            We could not load your dashboard snapshot right now.
          </p>
        ) : (
          <>
            {status?.nextActionLabel ? (
              <p style={{ margin: "0.6rem 0 0", color: "#4b5563" }}>
                Suggested next action: {status.nextActionLabel}
              </p>
            ) : null}

            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                flexWrap: "wrap",
                marginTop: "1rem",
              }}
            >
              {quickActions.map((action, index) => (
                <Link
                  key={`${action.to}-${index}`}
                  to={action.to}
                  style={index === 0 ? primaryActionStyle : actionLinkStyle}
                >
                  {action.label}
                </Link>
              ))}
            </div>
          </>
        )}
      </section>

      <section style={cardStyle}>
        <h3 style={{ margin: 0, color: "#0f172a" }}>Go-live checklist</h3>
        <ul
          style={{
            margin: "1rem 0 0",
            paddingLeft: "1.1rem",
            color: "#334155",
            lineHeight: 1.8,
          }}
        >
          <li>Complete business profile and verification.</li>
          <li>Add services, pricing, and deposit rules.</li>
          <li>Create at least one stylist or team member.</li>
          <li>Upload portfolio and cover media.</li>
          <li>Submit onboarding for internal review.</li>
        </ul>
      </section>
    </div>
  );
};

export default ProviderDashboardHomePage;